import { useState, useRef } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Mic, Send, Loader2, Sparkles, CheckCircle2, AlertCircle, Lightbulb, Trophy, X, RefreshCw,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

/**
 * Teach Back - active recall feature.
 * Student reads a topic → explains it back (typed or spoken via Whisper) → AI grades.
 *
 * Props:
 *  - subjectId, topicId, topicTitle, topicSummary
 *  - onClose (optional)
 */
const MIN_CHARS = 15;

const TeachBack = ({ subjectId, topicId, topicTitle, topicSummary, onClose }) => {
  const [explanation, setExplanation] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size > 0 && audioChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const formData = new FormData();
          formData.append("audio", blob, "voice.webm");
          const res = await fetch(`${BACKEND_URL}/api/tutor/stt`, {
            method: "POST",
            credentials: "include",
            body: formData,
          });
          if (res.status === 402) {
            toast.error("Voice transcription is a Pro feature - type your answer instead.");
            return;
          }
          if (!res.ok) throw new Error(`STT failed (${res.status})`);
          const data = await res.json();
          const txt = (data.text || "").trim();
          if (txt) setExplanation((cur) => (cur ? `${cur} ${txt}` : txt));
          else toast.info("I couldn't hear anything. Try again?");
        } catch (err) {
          console.error(err);
          toast.error("Couldn't transcribe - try typing instead.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      console.error(err);
      toast.error("Can't access your microphone. Check browser permissions.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const submit = async () => {
    const text = explanation.trim();
    if (text.length < MIN_CHARS) {
      toast.error(`Please write at least ${MIN_CHARS} characters (a sentence or two) first.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/tutor/teach-back", {
        subject_id: subjectId,
        topic_id: topicId,
        topic_title: topicTitle,
        topic_summary: topicSummary,
        user_explanation: text,
      });
      setFeedback(res);
      toast.success(`+${res.xp_earned} points earned!`);
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 401) msg = "Your session expired - please log in again.";
      else if (status === 402) msg = "This is a Pro feature - upgrade to use Teach Back.";
      else if (status === 400 && typeof detail === "string") msg = detail;
      else if (status >= 500) msg = "Our grader hit a snag. Please try again in a moment.";
      else msg = typeof detail === "string" ? detail : "Couldn't grade right now. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const tryAgain = () => {
    setExplanation("");
    setFeedback(null);
  };

  // ===== FEEDBACK VIEW =====
  if (feedback) {
    const overall = feedback.overall || 0;
    const tier = overall >= 80 ? "success" : overall >= 60 ? "warning" : "destructive";
    return (
      <div data-testid="teach-back-feedback" className="space-y-5">
        {/* Score header */}
        <div className="glass rounded-2xl p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy className={`w-5 h-5 text-${tier}`} />
              <h3 className="font-semibold text-lg">Examiner feedback</h3>
            </div>
            <span className="text-xs text-muted-foreground">+{feedback.xp_earned} points</span>
          </div>

          <div className="flex items-center gap-6 mb-4">
            <div className="text-center">
              <p className={`text-5xl font-bold text-${tier}`}>{overall}</p>
              <p className="text-xs text-muted-foreground mt-1">Overall</p>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-3">
              {[
                { label: "Accuracy", val: feedback.accuracy, weight: "50%" },
                { label: "Completeness", val: feedback.completeness, weight: "30%" },
                { label: "Clarity", val: feedback.clarity, weight: "20%" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl font-semibold">{s.val}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {feedback.one_liner && (
            <p className="text-sm italic text-muted-foreground border-l-2 border-primary pl-3 py-1">
              &quot;{feedback.one_liner}&quot;
            </p>
          )}
        </div>

        {/* What went well */}
        {feedback.what_went_well?.length > 0 && (
          <div className="glass rounded-2xl p-5 border border-success/30 bg-success/5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <h4 className="font-semibold">What you got right</h4>
            </div>
            <ul className="space-y-2 text-sm">
              {feedback.what_went_well.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-success mt-1">•</span><span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Missing concepts */}
        {feedback.missing_concepts?.length > 0 && (
          <div className="glass rounded-2xl p-5 border border-warning/30 bg-warning/5">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-warning" />
              <h4 className="font-semibold">Things to add next time</h4>
            </div>
            <ul className="space-y-2 text-sm">
              {feedback.missing_concepts.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-warning mt-1">•</span><span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Corrections */}
        {feedback.corrections?.length > 0 && (
          <div className="glass rounded-2xl p-5 border border-destructive/30 bg-destructive/5">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-destructive" />
              <h4 className="font-semibold">Important corrections</h4>
            </div>
            <ul className="space-y-2 text-sm">
              {feedback.corrections.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-destructive mt-1">•</span><span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          <Button onClick={tryAgain} variant="outline" className="rounded-full" data-testid="teach-back-retry">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try again
          </Button>
          {onClose && (
            <Button onClick={onClose} className="rounded-full">
              Continue studying
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ===== INPUT VIEW =====
  return (
    <div data-testid="teach-back-panel" className="space-y-4">
      <div className="glass rounded-2xl p-5 border border-primary/20 bg-primary/5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">Teach it back to me</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Pretend I&apos;m your patient or a colleague who knows nothing about <strong className="text-foreground">{topicTitle}</strong>.
          Explain it in your own words. <strong className="text-foreground">No looking back.</strong> Be specific - mention the key signs, causes, and how to manage it.
        </p>

        <textarea
          data-testid="teach-back-input"
          rows={6}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          placeholder="Start typing... or tap the microphone to speak"
          className="w-full px-4 py-3 rounded-xl bg-card border border-border text-sm focus:outline-none focus:border-primary/50 resize-none"
          disabled={submitting}
        />

        <div className="flex items-center justify-between mt-3">
          <div className={`text-xs ${explanation.trim().length < MIN_CHARS ? "text-warning" : "text-muted-foreground"}`} data-testid="teach-back-charcount">
            {explanation.trim().length} characters
            {explanation.trim().length < MIN_CHARS && ` - at least ${MIN_CHARS} to submit`}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="teach-back-mic-btn"
              onClick={recording ? stopRecording : startRecording}
              disabled={submitting || transcribing}
              variant="outline"
              size="sm"
              className={`rounded-full ${recording ? "bg-destructive/10 border-destructive text-destructive" : "border-warning/40 text-warning hover:bg-warning/10"}`}
            >
              {transcribing ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transcribing...</>
              ) : recording ? (
                <><span className="w-2 h-2 rounded-full bg-destructive mr-2 animate-pulse" /> Stop</>
              ) : (
                <><Mic className="w-4 h-4 mr-1" /> Speak</>
              )}
            </Button>
            <Button
              data-testid="teach-back-submit"
              onClick={submit}
              disabled={submitting}
              className="rounded-full"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Grading...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Submit explanation</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {recording && (
        <p className="text-xs text-center text-destructive animate-pulse">🔴 Recording... speak naturally</p>
      )}
    </div>
  );
};

export default TeachBack;
